import React from 'react';
import cn from 'classnames';
import SectionTitle from '../Shared/Tailwind/SectionTitle';
import InputField from '../Shared/Tailwind/InputField';
import { FIELD_TYPE_DATE, FIELD_TYPE_NUMBER, FIELD_TYPE_RADIO, FIELD_TYPE_SELECT, FIELD_TYPE_TEXT } from '../../utils/general';
import { isValidAverageHours, isValidGpa } from '../../utils/validation/award-payment';
import { AWARD_PAYMENT } from '../../constants/general';

const styles = {
  card: 'w-full self-stretch rounded-xl bg-white p-10 outline outline-1 outline-offset-[-1px] outline-brand-cyan flex flex-col justify-start items-start',
  stack: 'self-stretch flex flex-col justify-start items-start',
  title: 'justify-start text-blue text-3xl font-normal font-serif leading-10',
  sectionTop: 'self-stretch mt-4 pt-4 flex flex-col justify-start items-start text-left',
  sectionTopSmall: 'self-stretch pt-10 text-left',
  heading: 'justify-start text-brand-navy text-xl font-bold leading-8',
  bodyText: 'justify-start text-gray-800 text-base font-normal leading-6',
  gridWrap: 'self-stretch pt-6',
  twoColGrid: 'grid w-full grid-cols-1 gap-5 lg:grid-cols-2',
  fieldCol: 'w-full flex flex-col justify-start items-start min-w-0',
  fieldColSpan2: 'w-full lg:col-span-2 flex flex-col justify-start items-start min-w-0',
  labelWrap: 'self-stretch pb-2 text-left',
  label: 'justify-start text-brand-navy text-sm font-semibold leading-5',
  requiredMark: 'text-red-600',
  radioRowWrap: 'self-stretch pt-2 flex flex-col justify-start items-start',
  radioRow: 'self-stretch inline-flex flex-wrap justify-start items-start gap-4',
  radioItem: 'self-stretch inline-flex items-center gap-2',
  radioText: 'text-gray-900 text-base font-normal leading-6',
  graySection: 'self-stretch rounded-lg bg-gray-100 p-5',
  textInput:
    'w-full h-11 px-3 py-2.5 rounded-sm outline outline-1 outline-offset-[-1px] outline-brand-border text-gray-900 text-base font-normal leading-6',
};

type TextFieldType = typeof FIELD_TYPE_TEXT | typeof FIELD_TYPE_DATE | typeof FIELD_TYPE_NUMBER | typeof FIELD_TYPE_SELECT;

type SelectFieldOption = { label: string; value: string; disabled?: boolean };

type FormField = {
  label: string;
  name: string;
  value: string;
  type: TextFieldType;
  fullWidth?: boolean;
  required?: boolean;
  options?: SelectFieldOption[];
};

export type YourInformationFormState = {
  userId?: number;
  firstName: string;
  lastName: string;
  enrolledInSchool?: string;
  schoolInfo?: {
    currentMajor?: string;
    dateOfEnrollment?: string;
    plannedGraduationDate?: string;
    gpa?: string;
  };
  employmentStatus?: string;
  employmentInfo?: {
    placeOfEmployment?: string;
    industryTrade?: string;
    startDate?: string;
    paySchedule?: string;
    averageHoursWorkedPerWeek?: string;
    positionTitle?: string;
  };
};

interface RequestYourInformationProps {
  onFormChange?: (formState: YourInformationFormState) => void;
  initialValues?: YourInformationFormState | null;
  readOnly?: boolean;
  showRequiredErrors?: boolean;
}

const enrolledInSchoolOptions = [
  { label: 'Yes', value: AWARD_PAYMENT.ENROLLED_IN_SCHOOL },
  { label: 'No', value: AWARD_PAYMENT.NOT_ENROLLED_IN_SCHOOL },
];

const employmentStatusOptions = [
  { label: 'Currently Employed', value: AWARD_PAYMENT.EMPLOYED },
  { label: 'Not Employed', value: AWARD_PAYMENT.NOT_EMPLOYED },
  { label: 'Looking for Work', value: AWARD_PAYMENT.LOOKING_FOR_WORK },
];

export const INDUSTRY_TRADE_OPTIONS: SelectFieldOption[] = [
  { label: 'Agriculture & Landscaping', value: 'agriculture_landscaping' },
  { label: 'Construction', value: 'construction' },
  { label: 'Electrical, HVAC & Plumbing', value: 'electrical_hvac_plumbing' },
  { label: 'Manufacturing & Production', value: 'manufacturing_production' },
  { label: 'Welding & Fabrication', value: 'welding_fabrication' },
  { label: 'Transportation & Trucking', value: 'transportation_trucking' },
  { label: 'Automotive, Diesel, & Collision Repair', value: 'automotive_diesel_collision_repair' },
  { label: 'Facilities Maintenance & Custodial', value: 'facilities_maintenance_custodial' },
  { label: 'Food Service & Culinary', value: 'food_service_culinary' },
  { label: 'Hospitality', value: 'hospitality' },
  { label: 'Healthcare Support', value: 'healthcare_support' },
  { label: 'Early Childhood Education', value: 'early_childhood_education' },
  { label: 'Information Technology', value: 'information_technology' },
  { label: 'Retail', value: 'retail' },
  { label: 'Business & Customer Service', value: 'business_customer_service' },
  { label: 'Public Safety & Security', value: 'public_safety_security' },
];

const industryTradeOptionsSorted = [...INDUSTRY_TRADE_OPTIONS].sort((a, b) => a.label.localeCompare(b.label));

const schoolFields: FormField[] = [
  { label: 'Current Major/Field of Study', name: 'currentMajor', type: FIELD_TYPE_TEXT, value: '', fullWidth: true, required: true },
  { label: 'Date of Enrollment', name: 'dateOfEnrollment', type: FIELD_TYPE_DATE, value: '', required: true },
  { label: 'Planned Graduation Date', name: 'plannedGraduationDate', type: FIELD_TYPE_DATE, value: '', required: true },
  { label: 'GPA', name: 'gpa', type: FIELD_TYPE_NUMBER, value: '', required: true },
];

const employmentFields: FormField[] = [
  { label: 'Place of Employment', name: 'placeOfEmployment', type: FIELD_TYPE_TEXT, value: '', fullWidth: false, required: true },
  {
    label: 'Industry/Trade',
    name: 'industryTrade',
    type: FIELD_TYPE_SELECT,
    value: '',
    fullWidth: false,
    required: true,
    options: [{ label: 'Select Industry/Trade', value: '', disabled: true }, ...industryTradeOptionsSorted],
  },
  { label: 'Position Title', name: 'positionTitle', type: FIELD_TYPE_TEXT, value: '', required: true },
  { label: 'Start Date', name: 'startDate', type: FIELD_TYPE_DATE, value: '', required: true },
  { label: 'Pay Schedule', name: 'paySchedule', type: FIELD_TYPE_TEXT, value: '', required: true },
  { label: 'Average Hours Worked Per Week', name: 'averageHoursWorkedPerWeek', type: FIELD_TYPE_NUMBER, value: '', required: true },
];
const nameFields = [
  { label: 'First Name', name: 'firstName', value: '', required: true },
  { label: 'Last Name', name: 'lastName', value: '', required: true },
];

const RequestYourInformation = ({
  onFormChange,
  initialValues = null,
  readOnly = false,
  showRequiredErrors = false,
}: RequestYourInformationProps) => {
  const [nameFieldValues, setNameFieldValues] = React.useState<Record<string, string>>({
    firstName: initialValues?.firstName ?? '',
    lastName: initialValues?.lastName ?? '',
  });
  const [userId, setUserId] = React.useState<number | undefined>(initialValues?.userId);
  const [enrolledInSchool, setEnrolledInSchool] = React.useState<string | undefined>(initialValues?.enrolledInSchool);
  const [employmentStatus, setEmploymentStatus] = React.useState<string | undefined>(initialValues?.employmentStatus);

  const [schoolFieldValues, setSchoolFieldValues] = React.useState<Record<string, string>>({
    currentMajor: initialValues?.schoolInfo?.currentMajor ?? '',
    dateOfEnrollment: initialValues?.schoolInfo?.dateOfEnrollment ?? '',
    plannedGraduationDate: initialValues?.schoolInfo?.plannedGraduationDate ?? '',
    gpa: initialValues?.schoolInfo?.gpa ?? '',
  });

  const [employmentFieldValues, setEmploymentFieldValues] = React.useState<Record<string, string>>({
    placeOfEmployment: initialValues?.employmentInfo?.placeOfEmployment ?? '',
    industryTrade: initialValues?.employmentInfo?.industryTrade ?? '',
    startDate: initialValues?.employmentInfo?.startDate ?? '',
    paySchedule: initialValues?.employmentInfo?.paySchedule ?? '',
    averageHoursWorkedPerWeek: initialValues?.employmentInfo?.averageHoursWorkedPerWeek ?? '',
    positionTitle: initialValues?.employmentInfo?.positionTitle ?? '',
  });

  React.useEffect(() => {
    setUserId(initialValues?.userId);
    setNameFieldValues({
      firstName: initialValues?.firstName ?? '',
      lastName: initialValues?.lastName ?? '',
    });
    setEnrolledInSchool(initialValues?.enrolledInSchool);
    setEmploymentStatus(initialValues?.employmentStatus);
    setSchoolFieldValues({
      currentMajor: initialValues?.schoolInfo?.currentMajor ?? '',
      dateOfEnrollment: initialValues?.schoolInfo?.dateOfEnrollment ?? '',
      plannedGraduationDate: initialValues?.schoolInfo?.plannedGraduationDate ?? '',
      gpa: initialValues?.schoolInfo?.gpa ?? '',
    });
    setEmploymentFieldValues({
      placeOfEmployment: initialValues?.employmentInfo?.placeOfEmployment ?? '',
      industryTrade: initialValues?.employmentInfo?.industryTrade ?? '',
      startDate: initialValues?.employmentInfo?.startDate ?? '',
      paySchedule: initialValues?.employmentInfo?.paySchedule ?? '',
      averageHoursWorkedPerWeek: initialValues?.employmentInfo?.averageHoursWorkedPerWeek ?? '',
      positionTitle: initialValues?.employmentInfo?.positionTitle ?? '',
    });
  }, [initialValues]);

  React.useEffect(() => {
    onFormChange?.({
      userId,
      firstName: nameFieldValues.firstName,
      lastName: nameFieldValues.lastName,
      enrolledInSchool,
      schoolInfo: {
        currentMajor: schoolFieldValues.currentMajor,
        dateOfEnrollment: schoolFieldValues.dateOfEnrollment,
        plannedGraduationDate: schoolFieldValues.plannedGraduationDate,
        gpa: schoolFieldValues.gpa,
      },
      employmentStatus,
      employmentInfo: {
        placeOfEmployment: employmentFieldValues.placeOfEmployment,
        industryTrade: employmentFieldValues.industryTrade,
        startDate: employmentFieldValues.startDate,
        paySchedule: employmentFieldValues.paySchedule,
        averageHoursWorkedPerWeek: employmentFieldValues.averageHoursWorkedPerWeek,
        positionTitle: employmentFieldValues.positionTitle,
      },
    });
  }, [userId, nameFieldValues, enrolledInSchool, schoolFieldValues, employmentStatus, employmentFieldValues, onFormChange]);

  return (
    <div className={cn(styles.card)}>
      <SectionTitle className={styles.title}>Your Information</SectionTitle>

      <div className={cn(styles.sectionTop)}>
        <SectionTitle className={styles.heading}>Update/Confirm Your Information</SectionTitle>
      </div>

      <div className={cn(styles.sectionTopSmall)}>
        <div className={cn(styles.bodyText)}>Please review and update your information to make sure it&apos;s current:</div>
      </div>

      <div className={cn(styles.gridWrap)}>
        <div className={cn(styles.twoColGrid)}>
          {nameFields.map((field) => (
            <InputField
              key={field.name}
              name={field.name}
              label={field.label}
              type={FIELD_TYPE_TEXT}
              value={nameFieldValues[field.name] ?? ''}
              onChange={(event) =>
                setNameFieldValues((previousValues) => ({
                  ...previousValues,
                  [field.name]: event.target.value,
                }))
              }
              required={field.required}
              disabled={readOnly}
              wrapperClassName={styles.fieldCol}
              labelClassName={styles.label}
              requiredMarkClassName={styles.requiredMark}
              inputClassName={styles.textInput}
              showRequiredErrors={showRequiredErrors}
            />
          ))}
        </div>
      </div>

      <InputField
        name='enrolledInSchool'
        label='Enrolled in School?'
        type={FIELD_TYPE_RADIO}
        options={enrolledInSchoolOptions}
        value={enrolledInSchool}
        onChange={setEnrolledInSchool}
        required
        disabled={readOnly}
        showRequiredErrors={showRequiredErrors}
        wrapperClassName={styles.sectionTop}
        labelClassName={styles.label}
        requiredMarkClassName={styles.requiredMark}
        groupWrapperClassName={styles.radioRowWrap}
        groupClassName={styles.radioRow}
        radioItemClassName={styles.radioItem}
        radioLabelClassName={styles.radioText}
      />
      {enrolledInSchool === 'yes' && (
        <div className={cn(styles.gridWrap)}>
          <div className={cn(styles.graySection)}>
            <div className={cn(styles.twoColGrid)}>
              {schoolFields.map((field) => (
                <InputField
                  key={field.name}
                  name={field.name}
                  label={field.label}
                  type={field.type as any}
                  value={schoolFieldValues[field.name] ?? ''}
                  onChange={(event: { target: { value: any } }) => {
                    const newValue = event.target.value;
                    if (field.name === 'gpa') {
                      if (newValue === '') {
                        setSchoolFieldValues((previousValues) => ({
                          ...previousValues,
                          [field.name]: '',
                        }));
                        return;
                      }

                      if (!isValidGpa(newValue)) {
                        return;
                      }
                    }

                    setSchoolFieldValues((previousValues) => ({
                      ...previousValues,
                      [field.name]: newValue,
                    }));
                  }}
                  required={field.required}
                  disabled={readOnly}
                  wrapperClassName={field.fullWidth ? styles.fieldColSpan2 : styles.fieldCol}
                  labelClassName={styles.label}
                  requiredMarkClassName={styles.requiredMark}
                  inputClassName={styles.textInput}
                  showRequiredErrors={showRequiredErrors}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      <InputField
        name='employmentStatus'
        label='Employment Status'
        type={FIELD_TYPE_RADIO}
        options={employmentStatusOptions}
        value={employmentStatus}
        onChange={setEmploymentStatus}
        required
        disabled={readOnly}
        showRequiredErrors={showRequiredErrors}
        wrapperClassName={styles.sectionTop}
        labelClassName={styles.label}
        requiredMarkClassName={styles.requiredMark}
        groupWrapperClassName={styles.radioRowWrap}
        groupClassName={styles.radioRow}
        radioItemClassName={styles.radioItem}
        radioLabelClassName={styles.radioText}
      />

      {employmentStatus === 'employed' && (
        <div className={cn(styles.gridWrap)}>
          <div className={cn(styles.graySection)}>
            <div className={cn(styles.twoColGrid)}>
              {employmentFields.map((field) => (
                <InputField
                  key={field.name}
                  name={field.name}
                  label={field.label}
                  type={field.type}
                  options={field.options}
                  value={employmentFieldValues[field.name] ?? ''}
                  onChange={(eventOrValue: { target: { value: any } } | string) => {
                    const newValue = typeof eventOrValue === 'string' ? eventOrValue : eventOrValue.target.value;
                    if (field.name === 'averageHoursWorkedPerWeek') {
                      if (newValue === '') {
                        setEmploymentFieldValues((previousValues) => ({
                          ...previousValues,
                          [field.name]: '',
                        }));
                        return;
                      }

                      if (!isValidAverageHours(newValue)) {
                        return;
                      }
                    }

                    setEmploymentFieldValues((previousValues) => ({
                      ...previousValues,
                      [field.name]: newValue,
                    }));
                  }}
                  disabled={readOnly}
                  wrapperClassName={field.fullWidth ? styles.fieldColSpan2 : styles.fieldCol}
                  labelClassName={styles.label}
                  requiredMarkClassName={styles.requiredMark}
                  inputClassName={styles.textInput}
                  required={field.required}
                  showRequiredErrors={showRequiredErrors}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RequestYourInformation;
